import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BTN, EventChips, HealthBadge, LastFire, StatusBadge } from "@/components/postbacks/shared";
import { FireLogDialog } from "@/components/postbacks/FireLog";

function RowActions({ row, canEdit, onEdit, onChanged }) {
  const [fires, setFires] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work");
    } finally {
      setBusy(false);
    }
  };
  const test = () => run(async () => {
    const { data } = await api.post(`/postbacks/${row.id}/test`);
    if (data.ok) {
      toast.success(`Test sent. Their server answered ${data.status_code}`);
    } else {
      toast.error(`Test failed: ${data.error || "no answer"}`);
    }
    onChanged();
  });
  const openLog = async () => {
    setFires(null);
    const { data } = await api.get(`/postbacks/${row.id}/fires`);
    setFires(data.fires);
  };
  const toggle = () => run(async () => {
    const path = row.kind === "connection" ? `/postbacks/connections/${row.id}` : `/postbacks/${row.id}`;
    await api.patch(path, { enabled: !row.enabled });
    onChanged();
  });
  const remove = () => run(async () => {
    if (!window.confirm(`Delete "${row.name}"? We'll stop sending it.`)) return;
    await api.delete(`/postbacks/${row.id}`);
    toast.success("Deleted");
    onChanged();
  });
  const t = `postback-${row.id}`;
  return (
    <div className="flex flex-wrap gap-1">
      {canEdit && <button type="button" onClick={test} disabled={busy} className={BTN} data-testid={`${t}-test`}>Send test</button>}
      <button type="button" onClick={openLog} className={BTN} data-testid={`${t}-log`}>History</button>
      {canEdit && <button type="button" onClick={() => onEdit(row)} className={BTN} data-testid={`${t}-edit`}>Edit</button>}
      {canEdit && row.status === "approved" && (
        <button type="button" onClick={toggle} disabled={busy} className={BTN} data-testid={`${t}-toggle`}>
          {row.enabled ? "Pause" : "Resume"}
        </button>
      )}
      {canEdit && <button type="button" onClick={remove} disabled={busy} className={`${BTN} text-red-700 border-red-700`} data-testid={`${t}-delete`}>Delete</button>}
      {fires !== undefined && <FireLogDialog title={`${row.name}: what we sent`} fires={fires} onClose={() => setFires(undefined)} />}
    </div>
  );
}

function target(r, meta) {
  if (r.kind !== "connection") return r.url;
  return `${meta.platforms[r.platform]?.label || r.platform} · ${r.pixel_id} · token ${r.secret_hint || "saved"}`;
}

export function PostbackList({ rows, meta, onEdit, onChanged, empty = "No postbacks yet" }) {
  const labels = Object.fromEntries(meta.events.map((e) => [e.key, e.label]));
  const kind = empty.startsWith("No connections") ? "connections" : "postbacks";
  if (!rows.length) {
    return <div className="py-6 text-center label-mono" data-testid={`${kind}-empty`}>{empty}</div>;
  }
  return (
    <div className="divide-y divide-black" data-testid={`${kind}-list`}>
      {rows.map((r) => (
        <div key={r.id} className="py-3 grid gap-2 lg:grid-cols-[1.2fr_2fr_1.2fr_auto] lg:items-center" data-testid={`postback-row-${r.id}`}>
          <div>
            <div className="font-semibold text-sm" data-testid={`postback-${r.id}-name`}>{r.name}</div>
            <div className="flex flex-wrap gap-1">
              <StatusBadge row={r} testId={`postback-${r.id}-status`} />
              <HealthBadge row={r} testId={`postback-${r.id}-health`} />
            </div>
            {r.status === "rejected" && r.review_note && (
              <div className="text-[11px] text-red-700 mt-1" data-testid={`postback-${r.id}-note`}>Admin: {r.review_note}</div>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[11px] break-all text-zinc-700" data-testid={`postback-${r.id}-url`}>{target(r, meta)}</div>
            <div className="mt-1"><EventChips events={r.events} labels={labels} testId={`postback-${r.id}-events`} /></div>
          </div>
          <div className="text-[11px]">
            <div className="label-mono">Last sent</div>
            <LastFire fire={r.last_fire} testId={`postback-${r.id}-last`} />
            {r.last_test && (
              <div className={r.last_test.ok ? "text-zinc-500 mt-0.5" : "text-red-700 mt-0.5"} data-testid={`postback-${r.id}-last-test`}>
                Last test: {r.last_test.ok ? "OK" : `failed${r.last_test.error ? ` (${r.last_test.error})` : ""}`}
              </div>
            )}
          </div>
          <RowActions row={r} canEdit={meta.can_edit} onEdit={onEdit} onChanged={onChanged} />
        </div>
      ))}
    </div>
  );
}
